import { LicenceTypeProductInfoProps } from "../../../Utils/Types";
import { Vocabulary } from "../../../Utils/Vocabulary";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import DocumentButton from "../../DocumentButton";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { formatDate } from "../../../Utils/Utils";
import ModalButton from "../../ModalButton";
import { EditVehicle } from "../EditVehicle";
import { endpoints } from "../../../Utils/UrlEnum";
import { getVehicleLabel } from "../../Vehicles/VehicleUtils";
import { EditInstructor } from "../EditInstructor";
import ReplayIcon from "@mui/icons-material/Replay";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { ro } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField } from "@mui/material";
import { parseISO } from "date-fns";
import { getData } from "../../../Services/getData";
import { AxiosResponse } from "axios";

/**
 * A method that returns a list of LicenceTypeProductInfo rows.
 * It takes props from the parent component and returns an array of objects.
 * Each object contains a label and a value.
 * The value can be a string or a JSX element.
 * The method also adds a button to each row that calls a callback function.
 * @param props - The props from the parent component.
 * @returns - An array of objects.
 */
export const LicenceTypeProductInfo = ({
  getValues,
  theme,
  setValue,
  control,
  emptyRow,
  instructors,
  vehicles,
  saveOrder,
  exam,
  retryExam,
  clientDocuments,
  onDocumentSaved,
  productData,
  allDocumentTypes,
}: LicenceTypeProductInfoProps) => {
  // Get required document types from product
  const requiredDocumentTypes = productData?.documentTypes || [];

  // Safe accessor for nested form values
  const getNestedValue = (path: string, defaultValue = "") => {
    const value = getValues(path);
    return value !== undefined && value !== null ? value : defaultValue;
  };

  const getInstructorFullName = () => {
    const firstName = getNestedValue("instructor_id.user_id.firstName");
    const lastName = getNestedValue("instructor_id.user_id.lastName");
    return firstName || lastName
      ? `${firstName} ${lastName}`.trim()
      : Vocabulary.selectInstructor;
  };

  // Get appropriate icon based on slug
  const getIcon = (slug: string) => {
    switch (slug?.toLowerCase()) {
      case "psychological":
        return <PsychologyIcon fontSize="small" style={{ marginRight: 5 }} />;
      case "medical":
        return (
          <MedicalServicesIcon fontSize="small" style={{ marginRight: 5 }} />
        );
      case "criminal":
        return <GavelIcon fontSize="small" style={{ marginRight: 5 }} />;
      default:
        return <DescriptionIcon fontSize="small" style={{ marginRight: 5 }} />;
    }
  };

  // Map required document types to rows
  const documentRows = requiredDocumentTypes.map((docTypeId: string) => {
    // Find the document type object from allDocumentTypes
    const docType = allDocumentTypes?.find((dt) => dt._id === docTypeId);

    // Find existing document for this type by checking if any client document matches this type
    const existingDoc = clientDocuments?.find(
      (doc) =>
        (doc.documentTypeId as any)?._id === docTypeId ||
        doc.documentTypeId === docTypeId
    );

    return {
      label: (
        <>
          {getIcon(docType?.slug)}
          {Vocabulary.date + " " + docType?.name}
        </>
      ),
      value: formatDate(existingDoc?.expirationDate, "dd.MM.yyyy"),
      button: (
        <DocumentButton
          setValue={setValue}
          getValues={getValues}
          control={control}
          buttonTitle={
            existingDoc ? Vocabulary.edit : `${Vocabulary.add} ${docType?.name}`
          }
          documentType={docType?.name}
          onDocumentSaved={onDocumentSaved}
          allDocumentTypes={allDocumentTypes}
          existingDocument={existingDoc}
        />
      ),
    };
  });

  return [
    {
      label: Vocabulary.instructor,
      value: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <AccountBoxIcon
            style={{
              marginRight: "8px",
              color: theme.palette.iconBlue?.main,
            }}
          />
          {getInstructorFullName()}
        </div>
      ),
      button: (
        <ModalButton
          buttonIcon={null}
          buttonTitle={Vocabulary.addInstructor}
          modalTitle={Vocabulary.selectInstructor}
          saveAction={saveOrder}
          content={
            <EditInstructor
              searchUrl={`${endpoints.instructor.user}/paginated?page=0&perPage=100000`}
              getValues={getValues}
              control={control}
              onProductChange={async (e, product, reason, details) => {
                const userId = details.option._id;
                // Fetch instructor by user ID
                const instructorRes: AxiosResponse = await getData(
                  `${endpoints.instructor.main}/by-user/${userId}`
                );
                if (instructorRes?.data?._id) {
                  setValue(`instructor_id`, instructorRes.data._id);
                  setValue(`vehicle_id`, null);
                }
              }}
              instructors={instructors as any}
            />
          }
        />
      ),
    },
    {
      label: Vocabulary.vehicle,
      value: getVehicleLabel(getValues("vehicle_id")),
      button: (
        <ModalButton
          buttonIcon={null}
          buttonTitle={Vocabulary.addVehicle}
          modalTitle={Vocabulary.selectVehicle}
          saveAction={saveOrder}
          content={
            <EditVehicle
              getValues={getValues}
              control={control}
              searchUrl={`${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000`}
              onProductChange={(e, product, reason, details) => {
                setValue(`vehicle_id`, details.option._id);
              }}
              vehicles={vehicles as any}
              applyStyle={true}
            />
          }
        />
      ),
    },
    ...emptyRow,
    ...documentRows,
    ...(exam
      ? [
          {
            label: exam?.type,
            value: formatDate(exam?.examDate),
            button: (
              <ModalButton
                buttonTitle={
                  exam?.examDate ? Vocabulary.retryExam : Vocabulary.addExam
                }
                buttonIcon={<ReplayIcon />}
                saveAction={() => retryExam(exam)}
                content={
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ro}
                  >
                    <DatePicker
                      label={exam?.type}
                      value={
                        exam?.examDate ? `${parseISO(exam.examDate)}` : null
                      }
                      onChange={(newDate) => (exam.examDate = newDate)}
                      views={["month", "year", "day"]}
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                }
              />
            ),
          },
        ]
      : []),
  ];
};
